import React, { useEffect, useState, useCallback } from "react";
import {
  Brain, Users, Briefcase, Bot, Sparkles, Loader2, RefreshCw, ArrowRight,
  CheckCircle2, Clock, Zap, IndianRupee, Pause, Play, X, FileText, AlertCircle
} from "lucide-react";
import EarlyBirdAnalyticsCard from "./EarlyBirdAnalyticsCard";
import DoozySalesAnalyticsCard from "./DoozySalesAnalyticsCard";
import BusinessInfoCard from "./BusinessInfoCard";
import SuperAdminCredentialsCard from "./SuperAdminCredentialsCard";
import SuperAdminLoginMobileCard from "./SuperAdminLoginMobileCard";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const Lane = ({ icon: Icon, gradient, title, subtitle, count, ctaLabel, onCta, children, testId }) => (
  <div className="bg-white rounded-3xl shadow-lg overflow-hidden flex flex-col" data-testid={testId}>
    <div className={`px-5 py-4 text-white bg-gradient-to-r ${gradient} flex items-start justify-between`}>
      <div className="flex items-start gap-3">
        <div className="w-10 h-10 rounded-xl bg-white/15 flex items-center justify-center flex-shrink-0"><Icon size={20} /></div>
        <div>
          <h3 className="text-base font-bold leading-tight">{title}</h3>
          <p className="text-[11px] text-white/80">{subtitle}</p>
        </div>
      </div>
      <div className="text-right">
        <p className="text-3xl font-extrabold leading-none">{count}</p>
        <p className="text-[10px] uppercase tracking-wider text-white/70">total</p>
      </div>
    </div>
    <div className="flex-1 p-5 space-y-3">{children}</div>
    {ctaLabel && (
      <button onClick={onCta}
        className="px-5 py-3 text-sm font-semibold text-[#1E1B4B] hover:bg-[#FFFAF5] flex items-center justify-center gap-1.5 border-t border-gray-100"
        data-testid={`${testId}-cta`}>
        {ctaLabel} <ArrowRight size={14} />
      </button>
    )}
  </div>
);

const Stat = ({ label, value, tone = "slate" }) => {
  const tones = {
    slate: "bg-slate-50 text-slate-700",
    pink: "bg-[#FFF0F6] text-[#DC283C]",
    emerald: "bg-emerald-50 text-emerald-700",
    indigo: "bg-indigo-50 text-indigo-700",
    amber: "bg-amber-50 text-amber-700",
  };
  return (
    <div className={`flex items-center justify-between text-xs px-2.5 py-1.5 rounded-lg ${tones[tone] || tones.slate}`}>
      <span className="font-medium">{label}</span>
      <span className="font-bold">{value}</span>
    </div>
  );
};

const inr = (n) => `₹${(n || 0).toLocaleString('en-IN')}`;

const CTCEditModal = ({ employee, authAxios, onClose, onSaved }) => {
  const [amount, setAmount] = useState(employee?.monthly_ctc_inr || 0);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const save = async () => {
    setSaving(true);
    setError("");
    try {
      await authAxios.put(
        `${API_URL}/api/admin/doozyhr/employees/${encodeURIComponent(employee.username)}/ctc`,
        { monthly_ctc_inr: Number(amount) || 0, currency: "INR" }
      );
      onSaved();
    } catch (e) {
      setError(e?.response?.data?.detail || "Couldn't save CTC. Please retry.");
    }
    setSaving(false);
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-[60] flex items-center justify-center p-4" data-testid="ctc-edit-modal">
      <div className="bg-white rounded-2xl shadow-2xl max-w-sm w-full p-5">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-base font-bold text-[#1E1B4B]">Set CTC · {employee?.name || employee?.username}</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600" data-testid="ctc-edit-close"><X size={18} /></button>
        </div>
        <label className="text-xs font-semibold text-gray-600">Monthly cost-to-company (INR)</label>
        <div className="flex items-center gap-2 mt-1.5">
          <span className="px-3 py-2.5 bg-gray-100 rounded-xl text-sm">₹</span>
          <input type="number" value={amount} onChange={(e) => setAmount(e.target.value)}
            min="0" step="500" data-testid="ctc-amount-input"
            className="flex-1 px-3 py-2.5 rounded-xl border border-gray-200 focus:border-[#DC283C] focus:ring-2 focus:ring-[#DC283C]/20 outline-none text-sm" />
        </div>
        {error && (
          <div className="mt-3 bg-red-50 border border-red-200 text-red-700 rounded-xl p-2.5 text-xs" data-testid="ctc-error">
            {error}
          </div>
        )}
        <div className="flex gap-2 mt-4">
          <button onClick={onClose} className="flex-1 py-2.5 rounded-xl border border-gray-200 text-sm font-bold hover:bg-gray-50">Cancel</button>
          <button onClick={save} disabled={saving} data-testid="ctc-save-btn"
            className="flex-1 py-2.5 rounded-xl bg-gradient-to-r from-indigo-600 to-[#DC283C] text-white text-sm font-bold disabled:opacity-50 flex items-center justify-center gap-1.5">
            {saving ? <><Loader2 size={14} className="animate-spin" /> Saving...</> : "Save CTC"}
          </button>
        </div>
      </div>
    </div>
  );
};

const AgentCard = ({ agent, onPause, onResume, busyKey }) => {
  const isPaused = agent.status === "paused";
  const isBusy = busyKey === agent.key;
  return (
    <div className="border border-gray-100 rounded-xl p-2.5 hover:border-[#DC283C]/30 transition-colors" data-testid={`agent-card-${agent.key}`}>
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0 flex-1">
          <p className="text-sm font-bold text-[#1E1B4B] truncate flex items-center gap-1.5">
            <Sparkles size={12} className="text-[#DC283C]" /> {agent.name}
          </p>
          <p className="text-[10px] text-gray-500 truncate">{agent.summary}</p>
        </div>
        <div className="text-right flex-shrink-0">
          <p className="text-xs font-bold text-emerald-700">{inr(agent.live_cost_inr_30d)}</p>
          <p className="text-[9px] text-gray-400">/ 30d</p>
        </div>
      </div>
      <div className="flex items-center gap-1.5 mt-1.5 flex-wrap">
        <span className={`text-[9px] px-1.5 py-0.5 rounded font-bold flex items-center gap-1 ${
          isPaused ? "bg-amber-100 text-amber-700" : "bg-emerald-100 text-emerald-700"}`}>
          {isPaused ? <Pause size={9} /> : <CheckCircle2 size={9} />} {agent.status}
        </span>
        <span className="text-[9px] px-1.5 py-0.5 rounded bg-gray-100 text-gray-600 font-medium">{agent.category}</span>
        <span className="text-[9px] px-1.5 py-0.5 rounded bg-indigo-100 text-indigo-700 font-medium flex items-center gap-1">
          <Zap size={9} /> {agent.runs_30d} runs
        </span>
        <span className="text-[9px] text-gray-400 ml-auto">owner: {agent.owner}</span>
        {isPaused ? (
          <button onClick={() => onResume(agent.key)} disabled={isBusy}
            className="text-[10px] px-2 py-0.5 rounded bg-emerald-500 text-white font-bold flex items-center gap-1 hover:bg-emerald-600 disabled:opacity-50"
            data-testid={`agent-resume-${agent.key}`}>
            {isBusy ? <Loader2 size={9} className="animate-spin" /> : <Play size={9} />} Resume
          </button>
        ) : (
          <button onClick={() => onPause(agent.key)} disabled={isBusy}
            className="text-[10px] px-2 py-0.5 rounded bg-amber-500 text-white font-bold flex items-center gap-1 hover:bg-amber-600 disabled:opacity-50"
            data-testid={`agent-pause-${agent.key}`}>
            {isBusy ? <Loader2 size={9} className="animate-spin" /> : <Pause size={9} />} Pause
          </button>
        )}
      </div>
      {isPaused && agent.paused_meta?.paused_reason && (
        <p className="mt-1.5 text-[10px] text-amber-700 italic flex items-center gap-1" data-testid={`agent-pause-reason-${agent.key}`}>
          <AlertCircle size={10} /> {agent.paused_meta.paused_reason}
        </p>
      )}
    </div>
  );
};

const DoozyHRTab = ({ auth, authAxios, setActiveTab }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [busyKey, setBusyKey] = useState("");
  const [editingCtcFor, setEditingCtcFor] = useState(null);

  const fetchOverview = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const r = await authAxios.get(`${API_URL}/api/admin/doozyhr/overview`);
      setData(r.data);
    } catch (e) {
      setError(e?.response?.data?.detail || "Couldn't load DoozyHR overview. Please retry.");
    }
    setLoading(false);
  }, [authAxios]);

  useEffect(() => { fetchOverview(); }, [fetchOverview]);

  const goTo = (tabId) => { if (typeof setActiveTab === "function" && tabId) setActiveTab(tabId); };

  const handlePause = async (agentKey) => {
    const reason = window.prompt(`Pause ${agentKey}? Optional reason:`, "");
    if (reason === null) return;
    setBusyKey(agentKey);
    try {
      await authAxios.post(`${API_URL}/api/admin/doozyhr/agents/${agentKey}/pause`, { reason });
      await fetchOverview();
    } catch (e) {
      window.alert(e?.response?.data?.detail || "Couldn't pause agent.");
    }
    setBusyKey("");
  };

  const handleResume = async (agentKey) => {
    setBusyKey(agentKey);
    try {
      await authAxios.post(`${API_URL}/api/admin/doozyhr/agents/${agentKey}/resume`, {});
      await fetchOverview();
    } catch (e) {
      window.alert(e?.response?.data?.detail || "Couldn't resume agent.");
    }
    setBusyKey("");
  };

  if (loading) {
    return <div className="py-20 flex justify-center" data-testid="doozyhr-loading"><Loader2 size={32} className="animate-spin text-[#DC283C]" /></div>;
  }
  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-2xl p-6 text-center" data-testid="doozyhr-error">
        <p className="text-sm text-red-700 mb-3">{error}</p>
        <button onClick={fetchOverview} className="px-4 py-2 bg-red-500 text-white rounded-xl text-sm font-bold">
          <RefreshCw size={14} className="inline mr-1" /> Retry
        </button>
      </div>
    );
  }
  if (!data) return null;
  const { humans, vendors, agents, vision_tagline } = data;

  return (
    <div className="space-y-6" data-testid="doozyhr-tab">
      <div className="bg-gradient-to-r from-indigo-600 via-[#DC283C] to-[#FF9F1C] rounded-3xl p-6 sm:p-8 text-white shadow-xl">
        <div className="flex items-start gap-3">
          <div className="w-12 h-12 rounded-2xl bg-white/15 flex items-center justify-center flex-shrink-0"><Brain size={24} /></div>
          <div className="flex-1 min-w-0">
            <p className="text-[11px] uppercase tracking-[0.2em] text-white/70 font-semibold">DoozyHR · Phase 2</p>
            <h2 className="text-2xl sm:text-3xl font-extrabold leading-tight mt-1">{vision_tagline}</h2>
            <p className="text-sm text-white/85 mt-2 max-w-2xl">
              Live agent costs · pause/resume autonomy · vendor ledgers · per-employee CTC. One screen, one brain.
            </p>
          </div>
          <button onClick={fetchOverview}
            className="px-3 py-2 rounded-xl bg-white/15 hover:bg-white/25 text-xs font-bold flex items-center gap-1"
            data-testid="doozyhr-refresh"><RefreshCw size={12} /> Refresh</button>
          <button
            onClick={async () => {
              try {
                const res = await fetch(`${API_URL}/api/admin/booking-funnels/pdf`, {
                  credentials: "include",
                  headers: { "X-Admin-Auth": auth?.token || "" },
                });
                if (!res.ok) throw new Error(`HTTP ${res.status}`);
                const blob = await res.blob();
                const url = URL.createObjectURL(blob);
                const a = document.createElement("a");
                a.href = url; a.download = "DoozyGo_Booking_Funnels.pdf";
                document.body.appendChild(a); a.click();
                document.body.removeChild(a);
                URL.revokeObjectURL(url);
              } catch (e) {
                alert(`Could not download PDF: ${e.message}`);
              }
            }}
            className="ml-2 px-3 py-2 rounded-xl bg-white text-[#1E1B4B] hover:bg-[#FAF7F2] text-xs font-bold flex items-center gap-1"
            data-testid="doozyhr-download-funnels-pdf"
            title="Download all-funnels strategy map for ad-spend planning">
            <FileText size={12} /> Funnels PDF
          </button>
        </div>
        <div className="grid grid-cols-3 gap-4 mt-6">
          <div className="bg-white/10 backdrop-blur rounded-2xl p-4">
            <p className="text-[10px] uppercase tracking-wider text-white/70 font-semibold">Humans</p>
            <p className="text-3xl font-extrabold mt-1">{humans.total}</p>
            <p className="text-[10px] text-white/60 mt-0.5">{inr(humans.monthly_ctc_inr_total)}/mo CTC</p>
          </div>
          <div className="bg-white/10 backdrop-blur rounded-2xl p-4">
            <p className="text-[10px] uppercase tracking-wider text-white/70 font-semibold">Vendors</p>
            <p className="text-3xl font-extrabold mt-1">{vendors.total}</p>
            <p className="text-[10px] text-white/60 mt-0.5">{vendors.open_pos} open POs · {inr(vendors.outstanding_invoice_inr)} due</p>
          </div>
          <div className="bg-white/10 backdrop-blur rounded-2xl p-4">
            <p className="text-[10px] uppercase tracking-wider text-white/70 font-semibold">AI Agents</p>
            <p className="text-3xl font-extrabold mt-1">{agents.total}</p>
            <p className="text-[10px] text-white/60 mt-0.5">{inr(agents.live_cost_inr_30d_total)}/30d · {agents.runs_30d_total} runs</p>
          </div>
        </div>
      </div>

      <EarlyBirdAnalyticsCard authAxios={authAxios} />

      <DoozySalesAnalyticsCard authAxios={authAxios} />

      <BusinessInfoCard authAxios={authAxios} />

      <SuperAdminCredentialsCard authAxios={authAxios} role={auth?.role} />

      <SuperAdminLoginMobileCard authAxios={authAxios} role={auth?.role} />


      <div className="grid lg:grid-cols-3 gap-5">
        <Lane testId="doozyhr-humans-lane" icon={Users} gradient="from-[#1E1B4B] to-indigo-600"
          title="Humans" subtitle={`${humans.employees_with_ctc}/${humans.total} have CTC set`}
          count={humans.total} ctaLabel="Manage in Team Hub" onCta={() => goTo("team")}>
          {humans.pending_onboarding > 0 && (
            <div className="bg-amber-50 border border-amber-200 rounded-xl px-3 py-2 text-xs text-amber-800 flex items-center gap-2" data-testid="doozyhr-pending-onboarding">
              <Clock size={14} /> <span><b>{humans.pending_onboarding}</b> pending onboarding</span>
            </div>
          )}
          <Stat label="Monthly burn (CTC)" value={inr(humans.monthly_ctc_inr_total)} tone="indigo" />
          <div className="space-y-1.5">
            {(humans.by_role || []).slice(0, 4).map((r) => (
              <Stat key={r.role} label={r.role.replace(/_/g, ' ')} value={r.count} tone="indigo" />
            ))}
          </div>
          {(humans.recent || []).length > 0 && (
            <div>
              <p className="text-[10px] uppercase tracking-wider text-gray-400 font-semibold mb-1.5 mt-3">Recent · click to set CTC</p>
              <div className="space-y-1">
                {humans.recent.slice(0, 5).map((m) => (
                  <button key={m.username} onClick={() => setEditingCtcFor(m)}
                    className="w-full text-xs flex items-center justify-between px-2 py-1 rounded hover:bg-indigo-50"
                    data-testid={`humans-row-${m.username}`}>
                    <span className="truncate text-[#1E1B4B] font-medium text-left">{m.name || m.username}</span>
                    <span className={`text-[10px] font-bold ${m.monthly_ctc_inr > 0 ? "text-indigo-600" : "text-gray-400"}`}>
                      {m.monthly_ctc_inr > 0 ? inr(m.monthly_ctc_inr) : "Set CTC →"}
                    </span>
                  </button>
                ))}
              </div>
            </div>
          )}
        </Lane>

        <Lane testId="doozyhr-vendors-lane" icon={Briefcase} gradient="from-[#FF9F1C] to-[#DC283C]"
          title="Vendors" subtitle="Extended workforce on demand"
          count={vendors.total} ctaLabel="Manage Vendors" onCta={() => goTo("vendors")}>
          <Stat label="Open POs" value={vendors.open_pos} tone="amber" />
          <Stat label="Outstanding invoices" value={inr(vendors.outstanding_invoice_inr)} tone="amber" />
          {vendors.total === 0 && (
            <p className="text-xs text-gray-500">No vendors registered yet. Add decorators, photographers, balloon artists, planners — anyone you outsource a job to.</p>
          )}
          <div className="space-y-1.5">
            {(vendors.by_category || []).slice(0, 4).map((r) => (
              <Stat key={r.category} label={r.category} value={r.count} tone="amber" />
            ))}
          </div>
          {(vendors.recent || []).length > 0 && (
            <div>
              <p className="text-[10px] uppercase tracking-wider text-gray-400 font-semibold mb-1.5 mt-3">Recent vendors</p>
              <div className="space-y-1">
                {vendors.recent.slice(0, 4).map((v, i) => (
                  <div key={`${v.name}-${i}`} className="text-xs flex items-center justify-between px-2 py-1">
                    <span className="truncate text-[#1E1B4B] font-medium">{v.name || "Unnamed"}</span>
                    <span className="text-gray-400 text-[10px]">{v.city || v.category || ''}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
          <div className="bg-amber-50/50 border border-amber-100 rounded-xl px-3 py-2 text-[11px] text-amber-700 flex items-start gap-1.5">
            <FileText size={12} className="flex-shrink-0 mt-0.5" />
            <span>PO + invoice flow live — POST /api/admin/doozyhr/vendors/&#123;id&#125;/po · /invoice · /invoice/&#123;id&#125;/pay.</span>
          </div>
        </Lane>

        <Lane testId="doozyhr-agents-lane" icon={Bot} gradient="from-emerald-500 to-indigo-600"
          title="AI Agents" subtitle="The multiplier layer · live costs"
          count={agents.total}>
          <div className="bg-emerald-50 border border-emerald-200 rounded-xl px-3 py-2 text-xs text-emerald-800 flex items-center gap-2">
            <IndianRupee size={14} />
            <span>Live 30-day burn: <b>{inr(agents.live_cost_inr_30d_total)}</b> · <b>{agents.runs_30d_total}</b> runs</span>
          </div>
          <div className="space-y-2 max-h-[320px] overflow-y-auto pr-1">
            {(agents.items || []).map((a) => (
              <AgentCard key={a.key} agent={a} onPause={handlePause} onResume={handleResume} busyKey={busyKey} />
            ))}
          </div>
        </Lane>
      </div>

      {editingCtcFor && (
        <CTCEditModal employee={editingCtcFor} authAxios={authAxios}
          onClose={() => setEditingCtcFor(null)}
          onSaved={() => { setEditingCtcFor(null); fetchOverview(); }} />
      )}

      <div className="bg-[#FFFAF5] rounded-2xl p-5 text-xs text-[#475569]" data-testid="doozyhr-roadmap">
        <p className="font-bold text-[#1E1B4B] text-sm mb-1">Phase 2 shipped · Phase 3 next</p>
        <p>Next up: DoozySales (AI closer for warm leads), DoozyDispatch (auto-routing ops), DoozyFinance (live P&L + GST), DoozyContent (Reels + IG generators tied to bookings) — each a separate module bolting cleanly into this OS.</p>
      </div>
    </div>
  );
};

export default DoozyHRTab;
